import { ReactNode } from 'react'
import { XCircle, DollarSign, Tag, Truck, MapPin, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface ProductDetailsModalProps {
  product: {
    id: string
    title: string
    description: string | null
    price: number
    min_price: number | null
    category: string
    status: string
    delivery_types: {
      standard: boolean
      express: boolean
      pickup: boolean
    }
    pickup_address: string | null
    estimated_delivery: string | null
    images: string[]
  }
  onClose: () => void
  onAction: (action: 'approve' | 'reject') => void
}

export function ProductDetailsModal({ product, onClose, onAction }: ProductDetailsModalProps) {
  const renderDeliveryTypes = (): ReactNode[] => {
    return Object.entries(product.delivery_types || {})
      .map(([type, enabled]) => 
        enabled ? (
          <div key={type} className="flex items-center p-4 bg-gray-50 rounded-lg">
            <Truck className="h-5 w-5 text-gray-400 mr-2" />
            <span className="text-sm text-gray-900 capitalize">
              {type === 'standard' ? 'Standard' :
               type === 'express' ? 'Express' : 'Retrait'}
            </span>
          </div>
        ) : null
      )
      .filter((node): node is React.ReactElement => node !== null)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <h3 className="text-xl font-bold text-gray-900">
              Product Details
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-6 space-y-6">
            {/* Images */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Images
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {product.images?.map((url, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={url}
                      alt={`${product.title} - Image ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Basic Information */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                General Information
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Title</label>
                  <p className="mt-1 text-sm text-gray-900">{product.title}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Category</label>
                  <div className="mt-1 flex items-center">
                    <Tag className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-sm text-gray-900">{product.category}</span>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-500">Description</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{product.description}</p>
                </div>
              </div>
            </div>

            {/* Price and Negotiation */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Price and Negotiation
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Price</label>
                  <div className="mt-1 flex items-center">
                    <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-sm text-gray-900">{product.price}</span>
                  </div>
                </div>
                {product.min_price && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Minimum Price</label>
                    <div className="mt-1 flex items-center">
                      <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900">{product.min_price}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Options */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Delivery Options
              </h4>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {renderDeliveryTypes()}
                </div>

                {product.pickup_address && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pickup Address</label>
                    <div className="mt-1 flex items-start">
                      <MapPin className="h-4 w-4 text-gray-400 mr-1 mt-0.5" />
                      <span className="text-sm text-gray-900">{product.pickup_address}</span>
                    </div>
                  </div>
                )}

                {product.estimated_delivery && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Estimated Delivery Time</label>
                    <div className="mt-1 flex items-center">
                      <Clock className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900">{product.estimated_delivery} days</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            {product.status === 'pending' && (
              <div className="pt-6 border-t flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={onClose}
                >
                  Close
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => onAction('reject')}
                >
                  Reject
                </Button>
                <Button
                  variant="primary"
                  onClick={() => onAction('approve')}
                >
                  Approve
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}